import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal } from "lucide-react";

const CATEGORIES = ["all", "Kurtis & Tunics", "Suit Sets", "Anarkalis", "Fusion Dresses"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/settings").then((r) => setCats(["all", ...(r.data.categories || [])])).catch(() => setCats(CATEGORIES)); }, []);
  const CATS = cats.length ? cats : CATEGORIES;
  const category = params.get("category") || "all";
  const sort = params.get("sort") || "new";
  const size = params.get("size") || "";
  const minP = params.get("min_price") || "";
  const maxP = params.get("max_price") || "";
  const [q, setQ] = useState(params.get("q") || "");

  const load = () => {
    setLoading(true);
    const p = {};
    if (category && category !== "all") p.category = category;
    if (sort) p.sort = sort;
    if (size) p.size = size;
    if (q) p.q = q;
    if (minP) p.min_price = minP;
    if (maxP) p.max_price = maxP;
    api.get("/products", { params: p }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, sort, size, minP, maxP]);

  // Flatten: each color variant becomes its own card on the grid (links to PDP with that color pre-selected)
  const cards = [];
  products.forEach((p) => {
    const vs = (p.variants || []).filter((v) => v.color && (v.images || []).length);
    if (vs.length > 1) {
      vs.forEach((v) => cards.push({ p, color: v.color, image: v.images[0], key: `${p.id}-${v.color}` }));
    } else {
      cards.push({ p, color: "", image: "", key: p.id });
    }
  });

  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (v && v !== "all") np.set(k, v); else np.delete(k);
    setParams(np);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
      <div className="mb-8">
        <p className="accent-label">{cards.length} Styles</p>
        <h1 className="font-serif text-3xl lg:text-5xl font-bold text-rose-950 mt-2">{category === "all" ? "Shop All" : category}</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-8 sticky top-16 lg:top-20 z-30 bg-[#fdfbf7]/90 backdrop-blur py-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATS.map((c) => (
            <button key={c} onClick={() => setParam("category", c)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-accent border transition-colors ${category === c ? "bg-rose-950 text-amber-50 border-rose-950" : "border-rose-900/20 text-rose-950/70 hover:border-rose-950"}`} data-testid={`filter-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>
        <div className="flex gap-3 items-center">
          <form onSubmit={(e) => { e.preventDefault(); setParam("q", q); load(); }} className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search kurti, SKU..." className="w-40 h-10" data-testid="shop-search-input" />
          </form>
          <Select value={size || "all"} onValueChange={(v) => setParam("size", v === "all" ? "" : v)}>
            <SelectTrigger className="w-28 h-10" data-testid="filter-size"><SelectValue placeholder="Size" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Sizes</SelectItem>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={`${minP}-${maxP}`} onValueChange={(v) => { const [a, b] = v.split("-"); const np = new URLSearchParams(params); if (a) np.set("min_price", a); else np.delete("min_price"); if (b) np.set("max_price", b); else np.delete("max_price"); setParams(np); }}>
            <SelectTrigger className="w-36 h-10" data-testid="filter-price"><SelectValue placeholder="Price" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="-">All Prices</SelectItem>
              <SelectItem value="0-1500">Under ₹1500</SelectItem>
              <SelectItem value="1500-3000">₹1500 - ₹3000</SelectItem>
              <SelectItem value="3000-5000">₹3000 - ₹5000</SelectItem>
              <SelectItem value="5000-100000">Above ₹5000</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger className="w-40 h-10" data-testid="filter-sort"><SlidersHorizontal className="w-4 h-4 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">Newest</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
              <SelectItem value="rating">Top Rated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] bg-rose-900/5 rounded-xl animate-pulse" />)}</div>
      ) : cards.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground">No products found. Try a different filter.</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="shop-grid">
          {cards.map((c, i) => <ProductCard key={c.key} p={c.p} index={i} variantColor={c.color} variantImage={c.image} />)}
        </div>
      )}
    </div>
  );
}
